// modules/heroContactPattern.js
export default function initHeroContactPattern(root = document) {
    const c = root.querySelector('[data-hero-contact]');
    if (!c) return;

    const ctx = c.getContext('2d');
    const DPR = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    /* ===== дизайн / геометрія ===== */
    const COL          = '#F5F8FF';
    const FILL_ALPHA   = 0.30;
    const STROKE_ALPHA = 0.30;
    const STROKE_W_CSS = 2;
    const LINE_ALPHA   = 0.20;

    const BASE_H       = 423;   // «робоча» висота секції з макету
    const BLEED_TOP    = 40;    // винос лінії/кіл нагору
    const DESKTOP_R    = 127;
    const MOBILE_BP    = 1200;
    const MOBILE_MAX_R = 86;

    const SPREAD_X_K   = 1.00;
    const SPREAD_Y_K   = 0.78;

    const SEGMENTS     = 220;
    const INFLUENCE_R  = 127;
    const STRENGTH     = 0.22;
    const SMOOTH       = 0.18;

    const OFFSET_RIGHT_PX = -60;

    // DOM підготовка (без SCSS)
    const holder = c.parentElement || c;
    if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';
    holder.style.overflow = 'visible';     // щоб нічого не різало
    c.style.position = 'absolute';
    c.style.right = '0';
    c.style.top = '0';
    c.style.display = 'block';


    /* ===== pointer ===== */
    const P = { gx: 0, gy: 0, active: false };
    const setPtr = e => {
        const r = c.getBoundingClientRect();
        P.gx = e.clientX - r.left;
        P.gy = e.clientY - r.top;
        P.active = true;
    };
    c.addEventListener('pointermove', setPtr, { passive: true });
    c.addEventListener('pointerenter', setPtr, { passive: true });
    c.addEventListener('pointerleave', () => (P.active = false), { passive: true });

    /* ===== offscreen ===== */
    const fillBuf = document.createElement('canvas');
    const cutBuf  = document.createElement('canvas');
    const ftx     = fillBuf.getContext('2d');
    const ctxCut  = cutBuf.getContext('2d');

    /* ===== state ===== */
    let W = 0, H = 0;
    let R = DESKTOP_R, spX = 0, spY = 0, cx = 0, cy = 0;
    let rings = [];
    const TAU = Math.PI * 2;

    /* helpers */
    const vw = () => window.innerWidth || document.documentElement.clientWidth || 0;

    function rebuild() {
        // радіус і відцентровка
        R   = (vw() < MOBILE_BP) ? Math.min(DESKTOP_R, MOBILE_MAX_R) : DESKTOP_R;
        spX = R * SPREAD_X_K;
        spY = R * SPREAD_Y_K;

        const viewW = W / DPR;

        // X: від правого краю
        cx = viewW - R - OFFSET_RIGHT_PX;
        // Y: «візуальний центр» на базі макетної висоти + BLEED_TOP + динамічний верхній відступ
        cy = c.__visualCenterY || (BLEED_TOP + BASE_H / 2);

        // кільця
        const C = [
            { name: 'L', x: cx - spX, y: cy },
            { name: 'R', x: cx + spX, y: cy },
            { name: 'T', x: cx,       y: cy - spY },
            { name: 'B', x: cx,       y: cy + spY },
        ];

        rings = C.map(cc => {
            const nodes = [];
            for (let i = 0; i < SEGMENTS; i++) {
                const t  = (i / SEGMENTS) * TAU;
                const x0 = (cc.x + Math.cos(t) * R) * DPR;
                const y0 = (cc.y + Math.sin(t) * R) * DPR;
                nodes.push({ x0, y0, x: x0, y: y0 });
            }
            return { name: cc.name, cx: cc.x * DPR, cy: cc.y * DPR, r: R * DPR, nodes };
        });
    }

    // аналітично рахуємо скільки треба місця зверху/знизу, щоб не різало
    function computeBleeds() {
        const topMost    = cy - spY - R;   // мінімальний Y у CSS-пікселях
        const bottomMost = cy + spY + R;   // максимальний Y
        const needTop    = Math.max(0, -topMost);
        const needBottom = Math.max(0, bottomMost - BASE_H);
        return { needTop, needBottom };
    }

    function sizeCanvas(cssW, cssH) {
        c.width  = Math.round(cssW * DPR);
        c.height = Math.round(cssH * DPR);
        c.style.width  = cssW + 'px';
        c.style.height = cssH + 'px';
        W = c.width; H = c.height;

        fillBuf.width = W;  fillBuf.height = H;
        cutBuf.width  = W;  cutBuf.height  = H;
    }

    function resize() {
        const cssW_initial = holder.clientWidth || window.innerWidth || 1920;

        // 1) попередній розрахунок на базову висоту
        sizeCanvas(cssW_initial, BASE_H + BLEED_TOP); // невеликий запас
        c.__visualCenterY = BLEED_TOP + BASE_H / 2;
        rebuild();

        // 2) точні бліди
        const { needTop, needBottom } = computeBleeds();
        const cssH = Math.ceil(BASE_H + needTop + needBottom);

        // виставляємо контейнер/канвас
        holder.style.height = cssH + 'px';
        c.style.top = -needTop + 'px';

        // 3) остаточний розмір канваса та повторна побудова (щоб DPI/буфери збіглись)
        sizeCanvas(cssW_initial, cssH);
        c.__visualCenterY = needTop + BLEED_TOP + BASE_H / 2;
        rebuild();
    }

    function deformAndStroke() {
        const px = P.gx * DPR, py = P.gy * DPR, radius = INFLUENCE_R * DPR;

        ctx.save();
        ctx.strokeStyle = COL;
        ctx.globalAlpha = STROKE_ALPHA;
        ctx.lineWidth   = Math.max(1, STROKE_W_CSS * DPR);
        ctx.lineJoin = 'round'; ctx.lineCap = 'round';

        for (const ring of rings) {
            for (const n of ring.nodes) {
                let tx = n.x0, ty = n.y0;
                if (P.active) {
                    const dx = n.x0 - px, dy = n.y0 - py;
                    const dist = Math.hypot(dx, dy) || 1e-4;
                    if (dist < radius) {
                        const fall = 1 - dist / radius;
                        const mag  = STRENGTH * (fall * fall) * radius;
                        tx += (dx / dist) * mag;
                        ty += (dy / dist) * mag;
                    }
                }
                n.x += (tx - n.x) * SMOOTH;
                n.y += (ty - n.y) * SMOOTH;
            }
            const nodes = ring.nodes;
            ctx.beginPath();
            ctx.moveTo(nodes[0].x, nodes[0].y);
            for (let i = 1; i < nodes.length; i++) ctx.lineTo(nodes[i].x, nodes[i].y);
            ctx.closePath();
            ctx.stroke();
        }
        ctx.restore();
    }

    function drawFillWithExactHole() {
        const T = rings.find(r => r.name === 'T');
        const B = rings.find(r => r.name === 'B');
        if (!T || !B) return;

        ftx.clearRect(0, 0, W, H);
        ftx.save();
        ftx.fillStyle = COL;
        ftx.globalAlpha = FILL_ALPHA;

        // top
        ftx.beginPath();
        ftx.moveTo(T.nodes[0].x, T.nodes[0].y);
        for (let i = 1; i < T.nodes.length; i++) ftx.lineTo(T.nodes[i].x, T.nodes[i].y);
        ftx.closePath(); ftx.fill();

        // bottom
        ftx.beginPath();
        ftx.moveTo(B.nodes[0].x, B.nodes[0].y);
        for (let i = 1; i < B.nodes.length; i++) ftx.lineTo(B.nodes[i].x, B.nodes[i].y);
        ftx.closePath(); ftx.fill();
        ftx.restore();

        // отвір = перетин T ∩ B
        ctxCut.clearRect(0, 0, W, H);
        ctxCut.save();
        ctxCut.fillStyle = '#000';

        ctxCut.globalCompositeOperation = 'source-over';
        ctxCut.beginPath();
        ctxCut.moveTo(T.nodes[0].x, T.nodes[0].y);
        for (let i = 1; i < T.nodes.length; i++) ctxCut.lineTo(T.nodes[i].x, T.nodes[i].y);
        ctxCut.closePath(); ctxCut.fill();

        ctxCut.globalCompositeOperation = 'destination-in';
        ctxCut.beginPath();
        ctxCut.moveTo(B.nodes[0].x, B.nodes[0].y);
        for (let i = 1; i < B.nodes.length; i++) ctxCut.lineTo(B.nodes[i].x, B.nodes[i].y);
        ctxCut.closePath(); ctxCut.fill();
        ctxCut.restore();

        ftx.save();
        ftx.globalCompositeOperation = 'destination-out';
        ftx.drawImage(cutBuf, 0, 0);
        ftx.restore();

        ctx.drawImage(fillBuf, 0, 0);
    }

    function drawLine() {
        ctx.save();
        ctx.globalAlpha = LINE_ALPHA;
        ctx.fillStyle   = '#FFFFFF';
        const lh = Math.max(1, Math.round(1.3333 * DPR));
        // лінія по центру базової секції (з урахуванням верхнього відступу)
        const y = Math.round((c.__visualCenterY) * DPR) - Math.floor(lh / 2);
        ctx.fillRect(0, y, W, lh);
        ctx.restore();
    }

    function frame() {
        ctx.clearRect(0, 0, W, H);
        drawFillWithExactHole();
        deformAndStroke();
        drawLine();
        requestAnimationFrame(frame);
    }

    const ro = new ResizeObserver(() => resize());
    ro.observe(holder);
    ro.observe(c);

    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('orientationchange', resize, { passive: true });

    // go
    resize();
    requestAnimationFrame(frame);
}
